import { Injectable, isDevMode } from '@angular/core';
import { CompareHelper } from '@barthauer/abc/core';
import { catchErrorAndThrowEmpty, ErrorHandlingService, ErrorInfo, Warning } from '@barthauer/abc/core/error-handling';
import { Observable, of, Subject } from 'rxjs';
import { concatMap, tap } from 'rxjs/operators';
import { AbcCoreDataModule } from './abc-core-data.module';
import { AfterOfflineStorageAction, CachePersistenceAdapter } from './caching/cache-persistence';
import { CacheService } from './caching/cache.service';
import { DataService } from './data.service';
import {
  ObjectKey,
  PersistMode,
  PersistObjectModel,
  PersistObjectsModel,
  TargetColumnValue,
  TargetObjectData,
} from './models/query.model';
import { ParameterValue } from '@barthauer/abc/queries/parameters/core';

export interface IPersistable {
  id: string | null;
  objectType: string | number;
  persistenceCategory?: string;
  beforePersisting?();
  changesPersisted?(persistMode: PersistMode);
  revertChanges?();
}

export class FieldValueHistory {
  originalValue: any;
  newValue: any;
  get hasChanged(): boolean {
    //This is intened to be != instead of !== , to check again values that are the same but with different type
    return this.originalValue != this.newValue;
  }
}

export class PendingObjectPackage {
  object: IPersistable;
  persistMode: PersistMode;
  fieldValueHistories: {} = {};

  get hasAnyChanges(): boolean {
    if (this.persistMode === PersistMode.Delete) {
      return true;
    } else {
      for (let fieldName in this.fieldValueHistories) {
        const fieldHistory: FieldValueHistory = this.fieldValueHistories[fieldName];
        if (fieldHistory.hasChanged) {
          return true;
        }
      }
      return false;
    }
  }

  setOriginalFieldHistoryValue(fieldName: string, setter) {
    if (this.fieldValueHistories[fieldName]) {
      setter(this.fieldValueHistories[fieldName].originalValue);
    }
  }
}

export class PersistObjectsPackage {
  persistObjectsModel: PersistObjectsModel;
  treatedObjects: IPersistable[];
}

export class PersistPendingChangesPackage {
  category: string;
  pendingObjects: {};
  acceptOfflineStorage: () => AfterOfflineStorageAction;
  onError: (err) => void;
  onSuccess: () => void;
}
export class PendingObjectsChangedPackage {
  category: string;
  hasAnyChanges: boolean;
}

@Injectable({
  providedIn: AbcCoreDataModule,
})
export class PersistenceService {
  private pendingObjects: {} = {};
  private persistPendingChangesSubject: Subject<PersistPendingChangesPackage> =
    new Subject<PersistPendingChangesPackage>();
  private pendingObjectsChangedSubject: Subject<PendingObjectsChangedPackage> =
    new Subject<PendingObjectsChangedPackage>();
  private afterPersistPendingChangesSubject: Subject<IPersistable[] | ErrorInfo> = new Subject<
    IPersistable[] | ErrorInfo
  >();
  public prepareModelBeforePersist: (model: PersistObjectsPackage) => Observable<PersistObjectsPackage>;
  public afterPersistPendingChanges$: Observable<IPersistable[] | ErrorInfo>;
  public pendingObjectsChanged$: Observable<PendingObjectsChangedPackage>;
  public objectId: string;

  public readonly cacheAdapter: CachePersistenceAdapter;
  constructor(
    data: DataService,
    private error: ErrorHandlingService,
    cache: CacheService,
  ) {
    this.cacheAdapter = new CachePersistenceAdapter(data, cache);

    this.afterPersistPendingChanges$ = this.afterPersistPendingChangesSubject.asObservable();
    this.pendingObjectsChanged$ = this.pendingObjectsChangedSubject.asObservable();

    this.persistPendingChangesSubject
      .asObservable()
      .pipe(
        concatMap((ppcp) =>
          this.createPersistObjectsModel(ppcp).pipe(
            concatMap((model) => {
              if (this.prepareModelBeforePersist) {
                return this.prepareModelBeforePersist(model);
              } else {
                return of(model);
              }
            }),
            concatMap((model) => {
              if (model) {
                if (isDevMode()) {
                  try {
                    console.log(JSON.stringify(model.persistObjectsModel, null, '  '));
                    this.objectId = model.persistObjectsModel.Objects[0][0].ObjectKey.Id;
                  } catch (err) {
                    console.error(err);
                  }
                }
                return this.executePersistObjectsQuery(model.persistObjectsModel, ppcp.acceptOfflineStorage).pipe(
                  tap((x) => {
                    const returnedObjects = <ObjectKey[]>x[0];
                    returnedObjects?.forEach((object, index) => {
                      const tos = model.treatedObjects[index];
                      if (tos && !tos.id) {
                        tos.id = object.Id;
                      }
                    });
                    this.notifyPersisted(ppcp, model);
                  }),
                  catchErrorAndThrowEmpty((err) => {
                    const errorInfo = this.error.getErrorInfo(err);
                    if (err instanceof Warning) {
                      this.notifyPersisted(ppcp, model, errorInfo);
                    } else {
                      if (ppcp.onError) {
                        ppcp.onError(err);
                      }
                      PersistenceService.copyPendingObjects(ppcp.pendingObjects, this.pendingObjects);
                      this.afterPersistPendingChangesSubject.next(errorInfo);
                    }
                  }),
                );
              } else {
                return of(null);
              }
            }),
          ),
        ),
      )
      .subscribe(
        () => {},
        (err) => {
          error.publish(err);
        },
      );
  }

  private static copyPendingObjects(source: {}, target: {}, category?: string) {
    for (let strCategory in source) {
      if (category === undefined || strCategory === category) {
        const sourceObjects: {} = source[strCategory];
        const targetObjects: {} = {};
        target[strCategory] = targetObjects;
        for (let strObjectKey in sourceObjects) {
          targetObjects[strObjectKey] = sourceObjects[strObjectKey];
        }
      }
    }
  }

  private notifyPersisted(ppcp: PersistPendingChangesPackage, model: PersistObjectsPackage, errorInfo?: ErrorInfo) {
    this.fireChangesPersisted(ppcp);
    if (errorInfo) {
      this.afterPersistPendingChangesSubject.next(errorInfo);
    } else {
      this.afterPersistPendingChangesSubject.next(model.treatedObjects);
      ppcp.onSuccess();
    }
  }

  public executePersistObjectsQuery(
    persistObjectsModel: PersistObjectsModel,
    acceptOfflineStorage?: () => AfterOfflineStorageAction,
  ): Observable<ArrayBuffer> {
    return this.cacheAdapter.executePersistObjectsQuery(persistObjectsModel, acceptOfflineStorage);
  }

  public executePersistObjectQuery(
    persistObjectModel: PersistObjectModel,
    acceptOfflineStorage?: () => AfterOfflineStorageAction,
  ): Observable<ArrayBuffer> {
    return this.cacheAdapter.executePersistObjectQuery(persistObjectModel, acceptOfflineStorage);
  }

  public clearPendingObjects(category?: string) {
    if (category === undefined) {
      this.pendingObjects = {};
    } else {
      this.pendingObjects[category] = {};
    }
    this.firePendingObjectsChanged(this.pendingObjects, category);
  }

  private firePendingObjectsChanged(pendingObjects: {}, category?: string) {
    let poc: PendingObjectsChangedPackage = new PendingObjectsChangedPackage();
    poc.category = category;
    if (!CompareHelper.isEmptyObject(pendingObjects)) {
      let po: PendingObjectPackage[];
      if (category === undefined) {
        po = Object.values(pendingObjects['undefined']);
      } else {
        po = Object.values(pendingObjects[category]);
      }
      poc.hasAnyChanges = po.some((x) => x.hasAnyChanges);
    } else {
      poc.hasAnyChanges = false;
    }
    this.pendingObjectsChangedSubject.next(poc);
  }
  private fireChangesPersisted(ppcp: PersistPendingChangesPackage) {
    const packs: PendingObjectPackage[] = this.collectPendingPackages(ppcp);
    packs.forEach((pack) => {
      if (pack.object.changesPersisted) {
        pack.object.changesPersisted(pack.persistMode);
      }
    });
  }

  private async fireBeforePersisting(ppcp: PersistPendingChangesPackage) {
    const packs: PendingObjectPackage[] = this.collectPendingPackages(ppcp);
    await Promise.all(packs.map((pack) => pack.object.beforePersisting?.()));
  }

  private collectPendingPackages(ppcp: PersistPendingChangesPackage): PendingObjectPackage[] {
    const pendingObjects: PendingObjectPackage[] = new Array<PendingObjectPackage>();
    if (ppcp.category === undefined) {
      for (let strCategory in ppcp.pendingObjects) {
        for (let strObjectKey in ppcp.pendingObjects[strCategory]) {
          const pack: PendingObjectPackage = ppcp.pendingObjects[strCategory][strObjectKey];
          pendingObjects.push(pack);
        }
      }
    } else {
      for (let strObjectKey in ppcp.pendingObjects[ppcp.category]) {
        const pack: PendingObjectPackage = ppcp.pendingObjects[ppcp.category][strObjectKey];
        pendingObjects.push(pack);
      }
    }
    return pendingObjects;
  }

  public addObjectForInsert(object: IPersistable) {
    const pack: PendingObjectPackage = this.ensurePendingObject(object);
    this.ensureFieldValueHistory(pack, 'Id', object.id, null);
    pack.persistMode = PersistMode.Insert;
  }

  public addObjectForMerge(object: IPersistable) {
    const pack: PendingObjectPackage = this.ensurePendingObject(object);
    this.ensureFieldValueHistory(pack, 'Id', object.id, null);
    pack.persistMode = PersistMode.Merge;
  }

  public addObjectForUpdate(object: IPersistable) {
    const pack: PendingObjectPackage = this.ensurePendingObject(object);
    pack.persistMode = PersistMode.Update;
  }

  public addObjectValue(object: IPersistable, fieldName: string, newValue: any, beforeValue?: any) {
    newValue = CompareHelper.isNullorEmpty(newValue) ? null : newValue;
    const pack: PendingObjectPackage = this.addValueForUpdateOrInsert(object, fieldName, newValue, beforeValue);
    if (pack.persistMode !== PersistMode.Insert && pack.persistMode !== PersistMode.Merge) {
      pack.persistMode = PersistMode.Update;
    }
  }
  public addObjectForDelete(object: IPersistable) {
    const objectKey = object.objectType + '_' + object.id;

    if (this.pendingObjects[object.persistenceCategory] && this.pendingObjects[object.persistenceCategory][objectKey]) {
      const pendingObject = this.pendingObjects[object.persistenceCategory][objectKey];
      if (pendingObject.persistMode === PersistMode.Insert || pendingObject.persistMode === PersistMode.Merge) {
        delete this.pendingObjects[object.persistenceCategory][objectKey];
        this.firePendingObjectsChanged(this.pendingObjects, object.persistenceCategory);
      } else if (pendingObject.persistMode === PersistMode.Update) {
        pendingObject.persistMode = PersistMode.Delete;
        pendingObject.fieldValueHistories = {};
      }
    } else {
      if (!this.pendingObjects[object.persistenceCategory]) {
        this.pendingObjects[object.persistenceCategory] = {};
      }
      const pack: PendingObjectPackage = new PendingObjectPackage();
      pack.object = object;
      pack.persistMode = PersistMode.Delete;
      this.pendingObjects[object.persistenceCategory][objectKey] = pack;
      this.firePendingObjectsChanged(this.pendingObjects, object.persistenceCategory);
    }
  }
  public removeObject(object: IPersistable) {
    const objectKey = object.objectType + '_' + object.id;

    if (this.pendingObjects[object.persistenceCategory] && this.pendingObjects[object.persistenceCategory][objectKey]) {
      delete this.pendingObjects[object.persistenceCategory][objectKey];
      this.firePendingObjectsChanged(this.pendingObjects, object.persistenceCategory);
    }
  }
  private addValueForUpdateOrInsert(
    object: IPersistable,
    fieldName: string,
    newValue: any,
    beforeValue: any,
  ): PendingObjectPackage {
    const pack: PendingObjectPackage = this.ensurePendingObject(object);
    this.ensureFieldValueHistory(pack, fieldName, newValue, beforeValue);
    this.firePendingObjectsChanged(this.pendingObjects, object.persistenceCategory);
    return pack;
  }
  private ensureFieldValueHistory(pack: PendingObjectPackage, fieldName: string, newValue: any, beforeValue: any) {
    let fieldHistory = pack.fieldValueHistories[fieldName];
    if (!fieldHistory) {
      fieldHistory = new FieldValueHistory();
      fieldHistory.originalValue = beforeValue;
      pack.fieldValueHistories[fieldName] = fieldHistory;
    }
    if (
      CompareHelper.isNullorEmpty(newValue) &&
      (pack.persistMode === PersistMode.Insert || pack.persistMode === PersistMode.Merge)
    ) {
      //avoid NULL inputs in query
      newValue = undefined;
    }
    fieldHistory.newValue = newValue;
  }
  private ensurePendingObject(object: IPersistable): PendingObjectPackage {
    const objectKey = object.objectType + '_' + object.id;

    if (!this.pendingObjects[object.persistenceCategory]) {
      this.pendingObjects[object.persistenceCategory] = {};
    }
    if (!this.pendingObjects[object.persistenceCategory][objectKey]) {
      const pack: PendingObjectPackage = new PendingObjectPackage();
      pack.object = object;
      this.pendingObjects[object.persistenceCategory][objectKey] = pack;
    }

    return this.pendingObjects[object.persistenceCategory][objectKey];
  }

  public persistPendingChanges(
    category?: string,
    acceptOfflineStorage?: () => AfterOfflineStorageAction,
    onerror?: (err) => void,
  ): void {
    this.persistPendingChangesCore(category, acceptOfflineStorage, onerror);
  }

  public async persistPendingChangesAsync(
    category?: string,
    acceptOfflineStorage?: () => AfterOfflineStorageAction,
  ): Promise<void> {
    await this.persistPendingChangesCore(category, acceptOfflineStorage);
  }

  private async persistPendingChangesCore(
    category?: string,
    acceptOfflineStorage?: () => AfterOfflineStorageAction,
    onError?: (err) => void,
  ): Promise<void> {
    if (
      !CompareHelper.isEmptyObject(this.pendingObjects) &&
      (category === undefined || !CompareHelper.isEmptyObject(this.pendingObjects[category]))
    ) {
      const ppcp: PersistPendingChangesPackage = new PersistPendingChangesPackage();
      ppcp.category = category;
      ppcp.pendingObjects = {};
      PersistenceService.copyPendingObjects(this.pendingObjects, ppcp.pendingObjects, category);
      await this.fireBeforePersisting(ppcp);
      this.clearPendingObjects(category);

      ppcp.acceptOfflineStorage = acceptOfflineStorage;
      ppcp.onError = onError;

      const successPromise = new Promise<void>((resolve, reject) => {
        ppcp.onSuccess = () => resolve();
        ppcp.onError = (err) => {
          onError?.(err);
          reject(err);
        };
      });

      this.persistPendingChangesSubject.next(ppcp);

      await successPromise;
    }
  }
  public revertPendingChanges(category?: string) {
    if (
      !CompareHelper.isEmptyObject(this.pendingObjects) &&
      (category === undefined || !CompareHelper.isEmptyObject(this.pendingObjects[category]))
    ) {
      for (let strCategory in this.pendingObjects) {
        if (category === undefined || strCategory === category) {
          for (let strObjectKey in this.pendingObjects[strCategory]) {
            const pack: PendingObjectPackage = this.pendingObjects[strCategory][strObjectKey];
            pack.object.revertChanges?.();
            for (const fieldname of Object.keys(pack.fieldValueHistories)) {
              const f: FieldValueHistory = pack.fieldValueHistories[fieldname];
              if (f) {
                f.newValue = f.originalValue;
              }
            }
            if (pack.persistMode === PersistMode.Delete) {
              delete this.pendingObjects[strCategory][strObjectKey];
            }
            this.firePendingObjectsChanged(this.pendingObjects, pack.object.persistenceCategory);
          }
        }
      }
    }
  }

  public getPendingObjectPackage(object: IPersistable): PendingObjectPackage {
    const objectKey = object.objectType + '_' + object.id;
    if (this.pendingObjects[object.persistenceCategory] && this.pendingObjects[object.persistenceCategory][objectKey]) {
      return this.pendingObjects[object.persistenceCategory][objectKey];
    } else {
      return null;
    }
  }

  private createPersistObjectsModel(ppcp: PersistPendingChangesPackage): Observable<PersistObjectsPackage> {
    let result: PersistObjectsPackage;
    const targetObjectDatas: TargetObjectData[] = new Array<TargetObjectData>();
    const treatedObjects: IPersistable[] = new Array<IPersistable>();

    const pendingObjects: PendingObjectPackage[] = new Array<PendingObjectPackage>();
    if (ppcp.category === undefined) {
      for (let strCategory in ppcp.pendingObjects) {
        for (let strObjectKey in ppcp.pendingObjects[strCategory]) {
          pendingObjects.push(ppcp.pendingObjects[strCategory][strObjectKey]);
        }
      }
    } else {
      for (let strObjectKey in ppcp.pendingObjects[ppcp.category]) {
        pendingObjects.push(ppcp.pendingObjects[ppcp.category][strObjectKey]);
      }
    }

    const parameters = {};
    for (let pack of pendingObjects) {
      let columnsToPersist: TargetColumnValue[];
      if (pack.persistMode !== PersistMode.Delete) {
        columnsToPersist = [];
        for (let fieldName in pack.fieldValueHistories) {
          const fieldHistory: FieldValueHistory = pack.fieldValueHistories[fieldName];
          if (fieldHistory.hasChanged) {
            const tcv: TargetColumnValue = new TargetColumnValue();
            tcv.Name = fieldName;
            let value = fieldHistory.newValue;
            if (!fieldName.endsWith('__Json') && typeof value === 'object') {
              for (const name in value) {
                parameters[name] = value[name];
                value = '$' + name;
                break;
              }
            }
            tcv.Value = value;
            if (tcv.Value !== undefined) {
              columnsToPersist.push(tcv);
            }
          }
        }
      }
      if (
        pack.persistMode === PersistMode.Insert ||
        pack.persistMode === PersistMode.Delete ||
        (columnsToPersist && columnsToPersist.length > 0)
      ) {
        const objectKey: ObjectKey = new ObjectKey();
        if (pack.persistMode !== PersistMode.Insert && pack.persistMode !== PersistMode.Merge) {
          objectKey.Id = pack.object.id;
        }
        objectKey.ObjectType = pack.object.objectType as string;

        const tod: TargetObjectData = new TargetObjectData();
        tod.ObjectKey = objectKey;
        tod.Mode = pack.persistMode;
        if (pack.persistMode !== PersistMode.Delete) {
          tod.TargetColumns = columnsToPersist;
        }
        targetObjectDatas.push(tod);
        treatedObjects.push(pack.object);
      }
    }

    if (targetObjectDatas.length > 0) {
      const query: PersistObjectsModel = new PersistObjectsModel();
      query.Objects = [targetObjectDatas];
      if (Object.keys(parameters)?.length) {
        query.Parameters = [];
        for (const name in parameters) {
          query.Parameters.push(new ParameterValue(name, parameters[name]));
        }
      }

      result = new PersistObjectsPackage();
      result.persistObjectsModel = query;
      result.treatedObjects = treatedObjects;
    }

    return of(result);
  }
}
